import { FormEvent, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { resetPassword } from "../lib/api";
import { saveToken } from "../lib/auth";

/** /reset/:token — consume a password-reset link. The user sets a
 * new password; on success we save the returned JWT and land them
 * signed in. The token is validated server-side (a used/expired one
 * comes back as an error here). */
export function ResetPassword() {
  const { token = "" } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await resetPassword(token, password);
      saveToken(resp.token);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not reset password");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center bg-white p-6 shadow rounded">
          <h1 className="text-xl font-semibold text-red-700">
            Invalid reset link
          </h1>
          <p className="mt-2 text-gray-600 text-sm">
            This link is missing its token. Ask an admin to send a new one.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen flex items-center justify-center px-4 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url('/agrilens-background.jpg')" }}
    >
      <div className="absolute inset-0 bg-white/25" aria-hidden="true" />
      <form
        onSubmit={onSubmit}
        className="relative z-10 w-full max-w-sm bg-white/95 backdrop-blur-sm shadow-xl rounded-lg p-6 space-y-4"
      >
        <div className="flex justify-center pb-1">
          <img src="/logo-app.png" alt="AgriLens.AI" className="h-16 w-auto" />
        </div>
        <h1 className="text-xl font-semibold text-agrilens-green-dark text-center">
          Set a new password
        </h1>

        {error && (
          <div className="bg-red-50 text-red-700 text-sm p-2 rounded">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-1">New password</label>
          <input
            type="password"
            value={password}
            minLength={8}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full border rounded px-3 py-2"
          />
          <p className="text-xs text-gray-500 mt-1">At least 8 characters.</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Confirm password
          </label>
          <input
            type="password"
            value={confirm}
            minLength={8}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className="w-full border rounded px-3 py-2"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-agrilens-green text-white py-2 rounded hover:bg-agrilens-green-dark disabled:opacity-50"
        >
          {submitting ? "Saving…" : "Set password & sign in"}
        </button>
      </form>
    </div>
  );
}
